import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { IntlProvider } from 'react-intl';
import parseParams from './utils/parseParams';
import App from './App';

const supportedLocales = [
  'af', 'ak', 'am', 'ar', 'as', 'ay', 'az', 'be', 'bg', 'bho', 'bm', 'bn', 'bs', 'ca', 'ceb', 'ckb', 'co', 'cs', 'cy',
  'da', 'de', 'doi', 'dv', 'ee', 'el', 'en', 'eo', 'es', 'et', 'eu', 'fa', 'fi', 'fr', 'fy', 'ga', 'gd', 'gl', 'gn',
  'gom', 'gu', 'ha', 'haw', 'he', 'hi', 'hmn', 'hr', 'ht', 'hu', 'hy', 'id', 'ig', 'ilo', 'is', 'it', 'iw', 'ja', 'jv',
  'jw', 'ka', 'kk', 'km', 'kn', 'ko', 'kri', 'ku', 'ky', 'la', 'lb', 'lg', 'ln', 'lo', 'lt', 'lus', 'lv', 'mai', 'mg',
  'mi', 'mk', 'ml', 'mn', 'mni-Mtei', 'mr', 'ms', 'mt', 'my', 'ne', 'nl', 'no', 'nso', 'ny', 'om', 'or', 'pa', 'pl',
  'ps', 'pt', 'qu', 'ro', 'ru', 'rw', 'sa', 'sd', 'si', 'sk', 'sl', 'sm', 'sn', 'so', 'sq', 'sr', 'st', 'su', 'sv',
  'sw', 'ta', 'te', 'tg', 'th', 'ti', 'tk', 'tl', 'tr', 'ts', 'tt', 'ug', 'uk', 'ur', 'uz', 'vi', 'xh', 'yi', 'yo',
  'zh-CN', 'zh-TW', 'zh', 'zu',
];

const isValidLocale = (language) => supportedLocales.includes(language);

const root = document.getElementById('root');
const { LAUNCH_DARKLY_KEY } = window.uniteus;
let LDProvider;

async function loadLaunchDarkly() {
  try {
    LDProvider = await asyncWithLDProvider({
      clientSideID: LAUNCH_DARKLY_KEY,
      user: {
        key: 'unknown',
        custom: {
          groupId: parseParams().providerId || 'anonymous',
        },
      },
      options: {
        bootstrap: 'localStorage',
        useReport: true,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load LaunchDarkly:', error);
    // fallback provider that just passes through
    LDProvider = ({ children }) => children;
  }
}

async function loadLocaleData(localeCode) {
  try {
    const messages = await import(`./compiled-lang/${localeCode}.json`);
    return messages.default;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`No resource file found for language: ${localeCode}. Falling back to English.`);
    const fallbackMessages = await import('./compiled-lang/en.json');
    return fallbackMessages.default;
  }
}

function LoadReactApp() {
  const { language } = parseParams();
  const [locale, setLocale] = useState(isValidLocale(language) ? language : 'en');
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMessages = async (lang) => {
    setLoading(true);
    try {
      const localeMessages = await loadLocaleData(lang);
      setMessages(localeMessages);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load messages:', error);
      // Fallback
      setMessages(await loadLocaleData('en'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages(locale);
  }, [locale]);

  const onChangeLanguage = (lang) => {
    if (!isValidLocale(lang)) return;
    setLocale(lang);
  };

  if (!LDProvider) {
    LDProvider = ({ children }) => children;
  }

  if (loading || !messages) {
    return <div>Loading...</div>;
  }

  return (
    <IntlProvider defaultLocale="en" locale={locale} messages={messages}>
      <LDProvider>
        <App onChangeLanguage={onChangeLanguage} locale={locale} />
      </LDProvider>
    </IntlProvider>
  );
}

loadLaunchDarkly().then(() => {
  ReactDOM.render(<LoadReactApp />, root);
}).catch((error) => {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize app:', error);
  ReactDOM.render(<LoadReactApp />, root);
});
